import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, must, type RegisteredIssuer } from "../api/client";
import { useWallet } from "../wallet/context";
import { newPhrase } from "../wallet/derive";
import { rootMultikeyFromPhrase } from "../wallet/wallet";
import { DidLabel } from "../components/DidLabel";
import { forgetDidName, useDidName } from "../did/useDidName";
import { MAX_NAME } from "../did/displayName";
import { DelegationCard } from "../components/DelegationCard";
import { keyPhrase } from "../wallet/present";
import { railDomain } from "../util";
import { fetchDidLog } from "../did/log";
import { fetchMyDelegations } from "../wallet/fetchDelegations";
import type { LogEntry } from "../crypto/webvh";

/** /me: this browser's identity. Keys are yours; lose them and the chain is gone. */
export function MePage() {
  const { wallet, identity, ready, refresh } = useWallet();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const back = params.get("return");
  const [mode, setMode] = useState<"none" | "create" | "restore" | "import">("none");
  const [candidates, setCandidates] = useState<string[]>([]);
  const [keyFile, setKeyFile] = useState("");
  const [offerLink, setOfferLink] = useState("");
  const [phrase, setPhrase] = useState("");
  const [method, setMethod] = useState<"webvh" | "key">("webvh");
  const [did, setDid] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [shownPhrase, setShownPhrase] = useState("");
  const [claims, setClaims] = useState<any[]>([]);
  const [nameVersion, setNameVersion] = useState(0);
  const currentName = useDidName(identity?.method === "webvh" ? identity.did : undefined, nameVersion);
  const [nameInput, setNameInput] = useState("");
  // The DID's log: its devices, and whether this browser can change it.
  const [log, setLog] = useState<LogEntry[]>();
  const [logVersion, setLogVersion] = useState(0);
  useEffect(() => {
    if (identity?.method !== "webvh") { setLog(undefined); return; }
    fetchDidLog(identity.did).then(setLog, () => setLog(undefined));
  }, [identity?.did, identity?.method, logVersion, nameVersion]);
  // Issuer names from the public registry, for the delegation cards.
  const [issuers, setIssuers] = useState<Record<string, RegisteredIssuer>>({});
  useEffect(() => {
    if (!identity?.delegations.length) return;
    api.GET("/registry/issuers").then((r) => setIssuers(Object.fromEntries((r.data?.issuers ?? []).filter((i) => i.name).map((i) => [i.did, i]))));
  }, [identity?.delegations.length]);

  useEffect(() => {
    if (identity?.method === "webvh")
      api.GET("/dids/{did}/chain", { params: { path: { did: identity.did } } }).then((r) => setClaims(r.data?.claims ?? []));
  }, [identity]);

  const done = () => { if (back) nav(back); };
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setErr("");
    try { await fn(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  const create = () => run(async () => {
    const p = newPhrase();
    const { genesis } = await wallet.fromPhrase(p, { method, domain: railDomain });
    if (genesis) await must(api.POST("/did", { body: { entry: genesis as any } }));
    setShownPhrase(p); await refresh();
  });
  // Restore: re-derive #root from the phrase and find its DID on the rail
  // (GET /dids/by-key/{multikey}); nothing needs to be typed but the phrase.
  const restore = () => run(async () => {
    if (method === "key") { await wallet.fromPhrase(phrase, { method }); await refresh(); return done(); }
    let chosen = did;
    if (!chosen) {
      const mk = rootMultikeyFromPhrase(phrase);
      const found = (await must(api.GET("/dids/by-key/{multikey}", { params: { path: { multikey: mk } } }))).dids
        .filter((d) => d.current).map((d) => d.did);
      if (!found.length) throw new Error("No DID on this rail lists this phrase's key. Check the phrase, or create a new identity.");
      if (found.length > 1) { setCandidates(found); return; }
      chosen = found[0];
    }
    await wallet.fromPhrase(phrase, { method: "webvh", create: false, did: chosen });
    wallet.delegationFetch = await fetchMyDelegations(wallet);   // delegations follow the DID
    await refresh(); done();
  });
  const importKey = () => run(async () => {
    const parsed = JSON.parse(keyFile);
    const entry = parsed.private_jwk ? parsed : Object.values(parsed).find((v: any) => v?.private_jwk);
    if (!entry) throw new Error("no identity with a private key in that file");
    await wallet.fromKeyFile(entry as any); await refresh(); done();
  });
  // The self-asserted name: a did:webvh update signed by #root (POST /did/{did}/keys).
  const saveName = (name: string | null) => run(async () => {
    const did = identity!.did;
    const entry = await wallet.displayNameUpdate(await fetchDidLog(did), name);
    try {
      await must(api.POST("/did/{did}/keys", { params: { path: { did } }, body: { entry: entry as any } }));
    } catch (e) {
      throw new Error(`The rail refused the name: ${(e as Error).message}`);
    }
    forgetDidName(did); setNameInput(""); setNameVersion((v) => v + 1);
  });
  // Remove a device: a DID update without its key (POST /did/{did}/keys).
  const removeDevice = (vmId: string) => run(async () => {
    const did = identity!.did;
    const entry = await wallet.removeDeviceUpdate(await fetchDidLog(did), vmId);
    await must(api.POST("/did/{did}/keys", { params: { path: { did } }, body: { entry: entry as any } }));
    setLogVersion((v) => v + 1);
  });
  const checkDelegations = () => run(async () => { wallet.delegationFetch = await fetchMyDelegations(wallet); await refresh(); });
  const openOffer = () => {
    const m = /get_credential_offer\/([^/?#\s]+)/.exec(offerLink) ?? /offer\/([^/?#\s]+)/.exec(offerLink);
    if (!m) return setErr("That is not a credential offer link.");
    nav(`/offer/${m[1]}`);
  };

  const devices = log ? wallet.devices(log) : undefined;
  const canChange = wallet.canUpdate(log);
  const removed = !!(identity?.browser && devices && !devices.some((d) => d.thisBrowser));

  // Nothing to tap until the wallet has loaded: then the page shows either the identity or the guard.
  if (!ready) return <div><h1>Your keys</h1><p className="note" data-testid="wallet-loading">Loading this browser's keys…</p></div>;

  if (!identity) return (
    <div>
      <h1>Your keys</h1>
      <p className="lead">Vouching and claiming need a key in this browser. Keys are yours; lose them and the chain is gone.</p>
      {err && <p className="error" role="alert">{err}</p>}
      <div className="card" data-testid="device-guard" data-ready="true">
        <b>Already have an identity on another device?</b> Link this browser to it, or restore from your phrase. One person, one identity: a second one starts your chain from nothing.
        <div className="row" style={{ marginTop: 12 }}>
          <button className="primary" onClick={() => nav("/me/link")} data-testid="link-this-browser">Link this browser</button>
          <button onClick={() => setMode("restore")} data-testid="mode-restore">Restore from your phrase</button>
        </div>
      </div>
      <p className="hint" style={{ marginTop: 16 }}>New here?</p>
      <div className="row">
        <button onClick={() => setMode("create")} data-testid="mode-create">Create a new identity</button>
        <button onClick={() => setMode("import")} data-testid="mode-import">Import a key file</button>
      </div>
      {mode === "import" && (
        <div className="card" style={{ marginTop: 16 }}>
          <label htmlFor="kf">Key file (JSON with a DID and an Ed25519 private JWK)</label>
          <textarea id="kf" value={keyFile} onChange={(e) => setKeyFile(e.target.value)} spellCheck={false} data-testid="key-file" />
          <p className="hint">For synthetic demo identities set up outside this browser. A key file is a secret: anyone holding it can sign as that DID.</p>
          <div className="row"><button className="primary" disabled={busy || !keyFile} onClick={importKey} data-testid="import-key">Import</button></div>
        </div>
      )}
      {(mode === "create" || mode === "restore") && (
        <div className="card" style={{ marginTop: 16 }}>
          <label htmlFor="method">Identity</label>
          <select id="method" value={method} onChange={(e) => setMethod(e.target.value as any)} data-testid="method">
            <option value="webvh">Permanent DID on {railDomain} (did:webvh)</option>
            <option value="key">Bare key only (did:key)</option>
          </select>
          {mode === "restore" && (<>
            <label htmlFor="phrase">Recovery phrase</label>
            <textarea id="phrase" value={phrase} onChange={(e) => setPhrase(e.target.value)} autoComplete="off" spellCheck={false} data-testid="phrase" />
            {candidates.length > 1 && (<>
              <label htmlFor="did">This key is in more than one DID; pick yours</label>
              <select id="did" value={did} onChange={(e) => setDid(e.target.value)} data-testid="did-choice">
                <option value="">Choose…</option>{candidates.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </>)}
            <div className="row" style={{ marginTop: 14 }}><button className="primary" disabled={busy} onClick={restore} data-testid="restore">Restore</button></div>
          </>)}
          {mode === "create" && <div className="row" style={{ marginTop: 14 }}><button className="primary" disabled={busy} onClick={create} data-testid="create">Create</button></div>}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <h1>Your identity</h1>
      {err && <p className="error" role="alert">{err}</p>}
      {shownPhrase && (
        <div className="card" style={{ marginBottom: 16 }}>
          <b>Write down your recovery phrase.</b> It is the only way back to this identity. It is shown once.
          <p className="phrase" data-testid="new-phrase">{shownPhrase}</p>
          <button onClick={() => { setShownPhrase(""); done(); }}>I have written it down</button>
        </div>
      )}
      <div className="card">
        <div className="mono" data-testid="my-did">{identity.did}</div>
        <p className="hint">{identity.method === "webvh"
          ? `did:webvh on ${identity.did.split(":")[3]}, portable; #root (from your phrase) can always change it${identity.linked ? ". This browser was linked from another device and holds no phrase" : ""}.`
          : "Bare did:key. Make it permanent by creating a did:webvh."}</p>
        {identity.browser && <p className="hint" data-testid="this-browser-key">This browser signs with {keyPhrase(identity.browser.kind)} (#{identity.browser.vmId ?? "browser-1"}){identity.browser.kind === "P-256" ? ", which can sign but never change your DID" : ""}.</p>}
      </div>
      {removed && <p className="error" role="alert" data-testid="device-removed">This browser was removed from your identity, so its signatures are no longer accepted. Forget it here, or link it again from a device that has your identity.</p>}
      {identity.method === "webvh" && devices && (<>
        <h2>Devices</h2>
        <div className="card" data-testid="devices">
          {devices.length === 0 && <p className="hint" style={{ margin: 0 }}>No browser keys; this identity signs with #root from your phrase.</p>}
          {devices.map((d) => (
            <div key={d.vmId} className="row" style={{ justifyContent: "space-between", alignItems: "center", padding: "6px 0" }} data-testid="device" data-vm={d.vmId}>
              <span><b>{d.thisBrowser ? "This browser" : d.label ?? `Device ${d.vmId.split("-")[1]}`}</b>
                <span className="hint"> · #{d.vmId} · {keyPhrase(d.kind)}{d.updateKey ? " · can change your identity" : ""}</span></span>
              {canChange && !d.thisBrowser && <button disabled={busy} onClick={() => removeDevice(d.vmId)} data-testid="device-remove">Remove</button>}
            </div>
          ))}
          {!canChange && <p className="hint" data-testid="devices-readonly">This browser cannot change your identity, so it cannot add or remove devices; use a device with your phrase or a linked Ed25519 device.</p>}
          <p className="hint" style={{ marginBottom: 0 }}>To add a device, open <b>/me/link</b> on it and open the link it shows here.</p>
        </div>
      </>)}
      <h2>Show a name with my DID</h2>
      {identity.method === "webvh" ? (
        <div className="card" data-testid="name-setting">
          <p className="hint" style={{ marginTop: 0 }}>Optional. The name is <b>public</b>; it is <b>permanent</b> in your DID's history, even if you remove it later; and it is shown as <b>self-asserted</b>, something you said, never as verified.</p>
          <div>Now: {currentName ? <DidLabel did={identity.did} version={nameVersion} /> : <span className="hint" data-testid="no-name">no name</span>}</div>
          <label htmlFor="dn">Name</label>
          <input id="dn" value={nameInput} maxLength={MAX_NAME} onChange={(e) => setNameInput(e.target.value)} placeholder="e.g. Asha K." data-testid="name-input" />
          {!canChange && <p className="note" data-testid="name-cannot">This browser cannot change your DID; set the name from a device that can.</p>}
          <div className="row" style={{ marginTop: 10 }}>
            <button className="primary" disabled={busy || !nameInput.trim() || !canChange} onClick={() => saveName(nameInput)} data-testid="name-save">Sign and show this name</button>
            {currentName && <button disabled={busy || !canChange} onClick={() => saveName(null)} data-testid="name-remove">Remove the name</button>}
          </div>
        </div>
      ) : <p className="note" data-testid="name-not-possible">A did:key has no document, so it cannot show a name. A did:webvh can.</p>}

      <h2>Delegations</h2>
      {wallet.delegationFetch?.text && <p className={wallet.delegationFetch.error ? "error" : "hint"} data-testid="delegations-fetched">{wallet.delegationFetch.text}</p>}
      {identity.delegations.length === 0 ? <p className="note">None. An issuer can authorise you to vouch on its behalf.</p> :
        identity.delegations.map((d) => <DelegationCard key={d.id} d={d} issuerName={issuers[d.issuer]?.name} basis={issuers[d.issuer]?.name_basis} domain={issuers[d.issuer]?.domain} />)}
      {identity.method === "webvh" && <div className="row" style={{ margin: "10px 0" }}><button disabled={busy} onClick={checkDelegations} data-testid="fetch-delegations">Check for delegations issued to my DID</button></div>}
      <label htmlFor="offer">Claim a credential an issuer offered you (paste the offer link)</label>
      <input id="offer" value={offerLink} onChange={(e) => setOfferLink(e.target.value)} placeholder="https://…/get_credential_offer/…" data-testid="offer-link" />
      <div className="row" style={{ marginTop: 10 }}><button disabled={!offerLink} onClick={openOffer} data-testid="open-offer">Open the offer</button>
        {back && <button className="primary" onClick={done}>Continue</button>}</div>
      {claims.length > 0 && (<><h2>Your claims</h2>{claims.map((c) => <p key={c.claim_id}><a href={`/c/${c.claim_id}`}>{c.claim.description}</a> · {c.rung.label}</p>)}</>)}
      <div className="row" style={{ marginTop: 24 }}>
        <a className="btn" href="/claim">Make a claim</a>
        <button onClick={() => run(async () => { await wallet.clear(); await refresh(); })}>Forget this browser's identity</button>
      </div>
    </div>
  );
}
